import { useEffect, useMemo, useState } from "react";
import Layout from "@/components/Layout";
import ProspectionMap from "@/components/ProspectionMap";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { http, formatApiError, formatWeek } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, MapPin, AlertCircle } from "lucide-react";

export default function AdminMap() {
  const [entries, setEntries] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterUser, setFilterUser] = useState("all");
  const [filterWeek, setFilterWeek] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const [ep, us] = await Promise.all([http.get("/prospection"), http.get("/users")]);
      setEntries(ep.data);
      setUsers(us.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const weeks = useMemo(
    () => Array.from(new Set(entries.map((e) => e.week_start))).sort().reverse(),
    [entries]
  );

  const filtered = entries.filter((e) => {
    if (filterUser !== "all" && e.user_id !== filterUser) return false;
    if (filterWeek !== "all" && e.week_start !== filterWeek) return false;
    return true;
  });

  const geocoded = filtered.filter((e) => e.lat != null && e.lng != null);
  const notGeocoded = filtered.filter((e) => e.lat == null || e.lng == null);

  const perUser = useMemo(() => {
    const map = new Map();
    for (const e of filtered) {
      const key = e.user_id;
      const cur = map.get(key) || {
        user_id: key,
        user_name: e.user_name,
        user_color: e.user_color,
        count: 0,
      };
      cur.count += 1;
      map.set(key, cur);
    }
    return Array.from(map.values()).sort((a, b) => b.count - a.count);
  }, [filtered]);

  return (
    <Layout>
      <div className="mb-8" data-testid="admin-map">
        <p className="label-eyebrow mb-2">Cartographie</p>
        <h1 className="font-display font-bold text-4xl sm:text-5xl tracking-tight">Carte de prospection</h1>
        <p className="mt-2 text-slate-600 max-w-2xl">
          Chaque point représente une adresse prospectée. Un point plus gros = plus de passages sur la zone.
          Une couleur par négociateur pour voir en un coup d'œil qui couvre quel secteur.
        </p>
      </div>

      <Card className="p-4 mb-6 flex flex-col sm:flex-row gap-3">
        <div className="flex-1">
          <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-1.5">Négociateur</div>
          <Select value={filterUser} onValueChange={setFilterUser}>
            <SelectTrigger data-testid="map-filter-user"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les négociateurs</SelectItem>
              {users.filter((u) => u.role === "collaborator").map((u) => (
                <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex-1">
          <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-1.5">Semaine</div>
          <Select value={filterWeek} onValueChange={setFilterWeek}>
            <SelectTrigger data-testid="map-filter-week"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes les semaines</SelectItem>
              {weeks.map((w) => (
                <SelectItem key={w} value={w}>{formatWeek(w)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 mb-6">
        <div className="lg:col-span-3">
          {loading ? (
            <Card className="p-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></Card>
          ) : (
            <ProspectionMap entries={geocoded} height={560} />
          )}
        </div>

        <Card className="p-5 h-fit" data-testid="map-legend">
          <div className="flex items-center gap-2 mb-4">
            <MapPin className="h-4 w-4 text-primary" />
            <div>
              <p className="label-eyebrow">Légende</p>
              <h3 className="font-display font-bold text-lg">Négociateurs</h3>
            </div>
          </div>
          {perUser.length === 0 ? (
            <div className="text-sm text-slate-500">Aucune adresse pour ces filtres.</div>
          ) : (
            <div className="space-y-2.5">
              {perUser.map((u) => (
                <div key={u.user_id} className="flex items-center gap-2.5" data-testid={`legend-${u.user_id}`}>
                  <span
                    className="h-4 w-4 rounded-full ring-2 ring-white shadow"
                    style={{ backgroundColor: u.user_color }}
                  />
                  <span className="font-semibold text-sm text-slate-900 flex-1 min-w-0 truncate">{u.user_name}</span>
                  <Badge variant="secondary" className="tabular-nums">{u.count}</Badge>
                </div>
              ))}
            </div>
          )}

          <div className="mt-5 pt-4 border-t border-border">
            <div className="text-xs text-slate-500 leading-relaxed">
              <div className="flex items-center gap-2 mb-1.5">
                <div className="h-3 w-3 rounded-full bg-primary/70 ring-1 ring-white" />
                <span>Passage unique</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="h-5 w-5 rounded-full bg-primary/70 ring-1 ring-white" />
                <span>Zone repassée plusieurs fois</span>
              </div>
            </div>
          </div>
        </Card>
      </div>

      {notGeocoded.length > 0 && (
        <Card className="p-5 border-amber-200 bg-amber-50" data-testid="not-geocoded-alert">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <div className="font-semibold text-amber-900 mb-1">
                {notGeocoded.length} adresse{notGeocoded.length > 1 ? "s" : ""} non localisée{notGeocoded.length > 1 ? "s" : ""}
              </div>
              <div className="text-sm text-amber-800">
                Ces adresses n'ont pas pu être placées sur la carte (adresse trop imprécise ou introuvable) :
              </div>
              <ul className="mt-2 text-xs text-amber-900 space-y-0.5">
                {notGeocoded.slice(0, 8).map((e) => (
                  <li key={e.id}>• {e.user_name} — {e.address}</li>
                ))}
                {notGeocoded.length > 8 ? <li className="italic">+ {notGeocoded.length - 8} autres</li> : null}
              </ul>
            </div>
          </div>
        </Card>
      )}
    </Layout>
  );
}
